import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog } from "@/components/ui/dialog";
import { DataTable, type Column } from "@/components/ui/data-table";
import { useAriApps, useCreateAriApp, useUpdateAriApp, useDeleteAriApp } from "@/hooks/use-ari-apps";
import type { AriApp } from "@/types";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

const columns: Column<AriApp>[] = [
  { key: "name", header: "Name" },
  { key: "allowed_origins", header: "Allowed Origins" },
  { key: "description", header: "Description" },
];

export default function AriAppsPage() {
  const { data, isLoading } = useAriApps();
  const createMut = useCreateAriApp();
  const updateMut = useUpdateAriApp();
  const deleteMut = useDeleteAriApp();

  const defaultApp = { name: "", password: "", allowed_origins: "*", description: null as string | null };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AriApp | null>(null);
  const [form, setForm] = useState(defaultApp);

  const openCreate = () => { setEditing(null); setForm(defaultApp); setOpen(true); };
  const openEdit = (a: AriApp) => {
    setEditing(a);
    setForm({ name: a.name, password: a.password, allowed_origins: a.allowed_origins, description: a.description ?? null });
    setOpen(true);
  };

  const handleSave = () => {
    if (editing) {
      updateMut.mutate({ id: editing.id, data: form as Partial<typeof form> }, { onSuccess: () => { setOpen(false); toast.success("ARI app updated"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    } else {
      createMut.mutate(form as typeof form, { onSuccess: () => { setOpen(false); toast.success("ARI app created"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    }
  };

  const handleDelete = (a: AriApp) => {
    deleteMut.mutate(a.id, { onSuccess: () => toast.success("ARI app deleted"), onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
  };

  const updateField = (field: string, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">ARI Apps</h1>
          <p className="text-sm text-muted-foreground mt-1">Asterisk REST Interface application registrations</p>
        </div>
        <Button onClick={openCreate} size="sm"><Plus size={14} /> Create ARI App</Button>
      </div>

      <DataTable columns={columns} data={data} isLoading={isLoading} getRowKey={(a) => a.id} onEdit={openEdit} onDelete={handleDelete} emptyMessage="No ARI apps configured." />

      <Dialog open={open} onClose={() => setOpen(false)} title={editing ? "Edit ARI App" : "Create ARI App"}>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Name * <HelpIcon text="ARI application identifier. Referenced by Stasis dialplan actions. Example: my-ivr-app, call-router." /></Label>
            <Input id="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password * <HelpIcon text="Password for ARI WebSocket authentication. Used by external applications connecting to Asterisk ARI." /></Label>
            <Input id="password" type="password" value={form.password} onChange={(e) => updateField("password", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="allowed_origins">Allowed Origins <HelpIcon text="CORS allowed origins for ARI HTTP requests. Default * allows all. Restrict in production: https://myapp.com." /></Label>
            <Input id="allowed_origins" value={form.allowed_origins} onChange={(e) => updateField("allowed_origins", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="description">Description <HelpIcon text="Human-readable description of the ARI application purpose." /></Label>
            <Textarea id="description" value={form.description ?? ""} onChange={(e) => updateField("description", e.target.value || null)} rows={2} />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>{editing ? "Update" : "Create"}</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
